import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { Store, ChevronDown, Plus, Check, Pencil, BarChart3 } from "lucide-react";
import { inr, Field } from "@/components/billing/BillingParts";

const blank = () => ({ name: "", gstin: "", address: "", city: "", state: "", state_code: "", phone: "", email: "", upi_id: "", invoice_prefix: "INV" });

function BusinessForm({ open, onOpenChange, biz, onSaved }) {
  const [f, setF] = useState(blank());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(biz ? { ...blank(), ...biz } : blank()); }, [open, biz]);
  const set = (k) => (v) => setF(s => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) return toast.error("Business name is required");
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(blank()).map(([k]) => [k, f[k] ?? ""]));
      const { data } = biz ? await api.patch(`/biz-multi/${biz.id}`, body) : await api.post("/biz-multi", body);
      toast.success(biz ? "Business updated" : `${data.name} added`); onSaved?.(data, !biz); onOpenChange(false);
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="business-form">
        <DialogHeader><DialogTitle className="font-heading tracking-tight text-xl">{biz ? `Edit ${biz.name}` : "Add a business / branch"}</DialogTitle><DialogDescription>Each business keeps its own items, customers, bills, staff and reports.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Business / shop name" value={f.name} onChange={set("name")} tid="biz-name" full />
          <Field label="GSTIN" value={f.gstin} onChange={set("gstin")} tid="biz-gstin" />
          <Field label="Invoice prefix" value={f.invoice_prefix} onChange={set("invoice_prefix")} tid="biz-prefix" placeholder="INV" />
          <Field label="Address" value={f.address} onChange={set("address")} tid="biz-address" full />
          <Field label="City" value={f.city} onChange={set("city")} tid="biz-city" />
          <Field label="State code (e.g. 27)" value={f.state_code} onChange={set("state_code")} tid="biz-state-code" />
          <Field label="Phone" value={f.phone} onChange={set("phone")} tid="biz-phone" />
          <Field label="UPI ID (for QR on bills)" value={f.upi_id} onChange={set("upi_id")} tid="biz-upi" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="biz-save" onClick={save} disabled={busy} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white">{busy ? "Saving…" : biz ? "Save changes" : "Add business"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BusinessSwitcher() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const isOwner = user?.role === "client" && !user?.staff_role;
  const load = () => api.get("/biz-multi").then(r => setData(r.data)).catch(() => {});
  useEffect(() => { if (isOwner) load(); }, [isOwner]); // eslint-disable-line
  if (!isOwner || !data) return null;
  const active = data.businesses.find(b => b.is_active) || data.businesses[0];

  const switchTo = async (b) => {
    if (b.is_active) return setOpen(false);
    try { await api.post("/biz-multi/switch", { client_id: b.id }); toast.success(`Switched to ${b.name}`); window.location.reload(); }
    catch (e) { toast.error("Switch failed", { description: e.response?.data?.detail || e.message }); }
  };
  const onSaved = (b, isNew) => { load(); if (isNew) switchTo(b); };

  return (
    <div className="relative" data-testid="business-switcher">
      <button data-testid="business-switcher-btn" onClick={() => setOpen(!open)} className="flex items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase font-semibold px-3 py-1.5 border border-zinc-300 hover:bg-zinc-900 hover:text-white transition-colors max-w-[200px]">
        <Store size={12} /> <span className="truncate">{active?.name}</span>{data.businesses.length > 1 ? <span className="tabular text-zinc-400">({data.businesses.length})</span> : null} <ChevronDown size={12} />
      </button>
      {open ? <>
        <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
        <div className="fixed sm:absolute right-3 left-3 sm:left-auto sm:right-0 top-14 sm:top-auto sm:mt-1 sm:w-80 bg-white border border-zinc-200 shadow-xl z-50" data-testid="business-menu">
          <div className="px-3 py-2 text-[10px] tracking-[0.14em] uppercase text-zinc-500 font-semibold border-b border-zinc-100">Your businesses</div>
          {data.businesses.map(b => (
            <div key={b.id} data-testid={`business-row-${b.id}`} className={`flex items-center gap-2 px-3 py-2 border-b border-zinc-100 text-xs ${b.is_active ? "bg-zinc-50" : "hover:bg-zinc-50"}`}>
              <button data-testid={`business-pick-${b.id}`} onClick={() => switchTo(b)} className="flex-1 text-left min-w-0">
                <div className="font-semibold flex items-center gap-1 truncate">{b.is_active ? <Check size={12} className="text-emerald-600" /> : null}{b.name}{b.is_primary ? <span className="text-[9px] text-zinc-400 uppercase tracking-wider">· main</span> : null}</div>
                <div className="text-[10px] text-zinc-500 tabular">{b.gstin || "No GSTIN"} · {b.bills_month} bills · {inr(b.sales_month)} this month{b.staff_count ? ` · ${b.staff_count} staff` : ""}</div>
              </button>
              <button data-testid={`business-edit-${b.id}`} onClick={() => { setEditing(b); setFormOpen(true); setOpen(false); }} className="p-1 border border-zinc-200 hover:bg-zinc-900 hover:text-white"><Pencil size={11} /></button>
            </div>
          ))}
          <Link to="/my-branches" data-testid="business-compare-link" onClick={() => setOpen(false)} className="w-full flex items-center justify-center gap-1 text-xs font-semibold py-2.5 border-b border-zinc-100 hover:bg-zinc-100 transition-colors"><BarChart3 size={13} /> Compare all branches</Link>
          <button data-testid="business-add-btn" onClick={() => { setEditing(null); setFormOpen(true); setOpen(false); }} className="w-full flex items-center justify-center gap-1 text-xs font-semibold py-2.5 hover:bg-zinc-900 hover:text-white transition-colors"><Plus size={13} /> Add business / branch</button>
        </div>
      </> : null}
      <BusinessForm open={formOpen} onOpenChange={setFormOpen} biz={editing} onSaved={onSaved} />
    </div>
  );
}
